import { useEffect, useState } from "react";

/**
 * Where a screen is with the answer it asked for.
 *
 * - `ready` — it is here (or the request failed), so show what there is.
 * - `waiting` — it is on its way, and too early to say anything. The space it
 *   will fill is left blank rather than filled with placeholders.
 * - `slow` — it has been on its way long enough to be worth admitting, so the
 *   placeholders go up.
 */
export type LoadPhase = "ready" | "waiting" | "slow";

/**
 * How long a load may take before it is worth showing placeholders for.
 *
 * Under this, a screen that shimmers and then fills reads as a flicker rather
 * than as loading — most calls from a warm cache answer inside it, and the
 * shimmer would be gone before the eye had settled on it. Over it, the screen
 * has visibly stalled and saying nothing starts to read as broken.
 */
export const PLACEHOLDER_DELAY_MS = 300;

/**
 * The phase a load is in, for the screens that shimmer while they wait.
 *
 * The point of the middle phase is what is *not* drawn: a page opened on a
 * fast connection, or one flicked back to, goes from blank to filled without
 * a shimmer in between, because the placeholders were never reached. Only a
 * load still going after `delayMs` gets them.
 *
 * The clock starts when `loading` turns true and is thrown away when it turns
 * false, so each fetch is timed on its own — a slow first load does not leave
 * the next one showing placeholders the instant it starts.
 */
export function useLoadPhase(
  loading: boolean,
  delayMs: number = PLACEHOLDER_DELAY_MS
): LoadPhase {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (!loading) {
      setSlow(false);
      return;
    }

    const timer = window.setTimeout(() => setSlow(true), delayMs);
    return () => window.clearTimeout(timer);
  }, [loading, delayMs]);

  // `loading` leads: the answer can land in the same render that the timer is
  // still holding `slow` from, and a screen with its data is never waiting.
  if (!loading) return "ready";
  return slow ? "slow" : "waiting";
}

export default useLoadPhase;
